// Offline storage utility - uses localStorage
// All data persists in browser - no internet needed

const KEYS = {
  PIN: "opg_pin",
  PROFILE: "opg_profile",
  TRANSACTIONS: "opg_transactions",
  CURRENCY: "opg_currency",
  ONBOARDED: "opg_onboarded",
  UNLOCKED_AT: "opg_unlocked_at",
  USER: "opg_user",
  OWNER: "opg_owner_email",
  BIO: "opg_bio_cred",
};

const UNLOCK_TTL_MS = 5 * 60 * 1000; // 5 minutes

export const storage = {
  // Google account (cached so app works offline after first sign-in)
  getUser() {
    const raw = localStorage.getItem(KEYS.USER);
    return raw ? JSON.parse(raw) : null;
  },
  setUser(user) {
    const prevOwner = localStorage.getItem(KEYS.OWNER);
    if (prevOwner && prevOwner !== user.email) {
      Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
    }
    localStorage.setItem(KEYS.OWNER, user.email);
    localStorage.setItem(KEYS.USER, JSON.stringify(user));
  },
  clearUser() {
    localStorage.removeItem(KEYS.USER);
  },

  // Profile
  getProfile() {
    const raw = localStorage.getItem(KEYS.PROFILE);
    return raw ? JSON.parse(raw) : null;
  },
  setProfile(profile) {
    localStorage.setItem(KEYS.PROFILE, JSON.stringify(profile));
  },

  // PIN (stored in simple obfuscated form; this is an offline demo app)
  getPin() {
    return localStorage.getItem(KEYS.PIN);
  },
  setPin(pin) {
    // basic obfuscation
    const b = btoa(`opg::${pin}::${Date.now()}`);
    localStorage.setItem(KEYS.PIN, b);
  },
  verifyPin(pin) {
    const stored = localStorage.getItem(KEYS.PIN);
    if (!stored) return false;
    try {
      const decoded = atob(stored);
      const parts = decoded.split("::");
      const ok = parts[1] === pin;
      if (ok) {
        localStorage.setItem(KEYS.UNLOCKED_AT, String(Date.now()));
      }
      return ok;
    } catch (e) {
      return false;
    }
  },
  hasPin() {
    return !!localStorage.getItem(KEYS.PIN);
  },
  isUnlocked() {
    const t = Number(localStorage.getItem(KEYS.UNLOCKED_AT) || 0);
    if (!t) return false;
    return Date.now() - t < UNLOCK_TTL_MS;
  },
  touchUnlock() {
    localStorage.setItem(KEYS.UNLOCKED_AT, String(Date.now()));
  },
  lock() {
    localStorage.removeItem(KEYS.UNLOCKED_AT);
  },

  // Currency
  getCurrency() {
    return localStorage.getItem(KEYS.CURRENCY) || "INR";
  },
  setCurrency(code) {
    localStorage.setItem(KEYS.CURRENCY, code);
  },

  // Transactions
  getTransactions() {
    const raw = localStorage.getItem(KEYS.TRANSACTIONS);
    return raw ? JSON.parse(raw) : [];
  },
  addTransaction(tx) {
    const list = this.getTransactions();
    const entry = {
      id: `TX${Date.now()}${Math.floor(Math.random() * 1000)}`,
      createdAt: new Date().toISOString(),
      status: "completed",
      ...tx,
    };
    list.unshift(entry);
    localStorage.setItem(KEYS.TRANSACTIONS, JSON.stringify(list));
    return entry;
  },
  clearAll() {
    Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
  },

  // Onboarding
  isOnboarded() {
    return !!localStorage.getItem(KEYS.ONBOARDED);
  },
  setOnboarded() {
    localStorage.setItem(KEYS.ONBOARDED, "1");
  },
};

export const KEYS_EXPORT = KEYS;
